import {
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { toast } from 'sonner'

import { subscribeRealtime } from '@/hooks/useRealtime'
import { getNotificationSummary, getNotifications } from '@/services/api/notification.service'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationStore } from '@/stores/notification.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { logger } from '@/utils/logger'

const POLL_INTERVAL_MS = 30_000

/**
 * Polls unread counts (cheap) every 30s and reloads the bell's first page only when its unread
 * count changes, the workspace changes or the tab becomes visible again.
 */
export function useNotificationPoller() {
  const accessToken = useAuthStore((state) => state.accessToken)
  // The backend scopes the bell to the token's workspace; reload when either changes.
  const currentWorkspaceId = useWorkspaceStore((state) => state.currentWorkspaceId)

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadFirstPage = useCallback(async (showLoading = false) => {
    const { setFirstPage, setLoading } = useNotificationStore.getState()
    if (showLoading) setLoading(true)
    try {
      setFirstPage(await getNotifications())
    } catch {
      logger.warn('Notification load failed — will retry')
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  const pollSummary = useCallback(async () => {
    if (!useAuthStore.getState().accessToken) {
      return
    }
    try {
      const summary = await getNotificationSummary()
      const { unreadCount, setSummary } = useNotificationStore.getState()
      setSummary(summary.unreadCount, summary.unreadByWorkspace)
      if (summary.unreadCount !== unreadCount) {
        await loadFirstPage()
      }
    } catch {
      logger.warn('Notification poll failed — will retry')
    }
  }, [loadFirstPage])

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      return
    }
    intervalRef.current = setInterval(() => {
      void pollSummary()
    }, POLL_INTERVAL_MS)
  }, [pollSummary])

  const stopPolling = useCallback(() => {
    if (!intervalRef.current) {
      return
    }
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }, [])

  // A notification created for this user while the stream is open: refresh the bell and show a toast
  useEffect(() => {
    if (!accessToken) {
      return
    }
    return subscribeRealtime('notification.created', (data) => {
      const id = (data as { id?: string } | null)?.id
      void loadFirstPage().then(() => {
        void pollSummary()
        const created = useNotificationStore.getState().notifications.find((n) => n.id === id)
        if (created) toast(created.title, { description: created.body })
      })
    })
  }, [accessToken, currentWorkspaceId, loadFirstPage, pollSummary])

  useEffect(() => {
    if (!accessToken) {
      return
    }

    // Initial load: the list, then the per-workspace counts (sequential, so the summary
    // compares against the loaded list and does not reload it again).
    void loadFirstPage(true).then(pollSummary)
    startPolling()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling()
        return
      }
      // User comes back to the tab
      void loadFirstPage().then(pollSummary)
      startPolling()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [accessToken, currentWorkspaceId, loadFirstPage, pollSummary, startPolling, stopPolling])
}
